import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Download, Share2, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportPage = () => {
  const { diagnosticId } = useParams();
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDiagnostic = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}`);
      setDiagnostic(response.data);
    } catch (err) {
      console.error('Error fetching diagnostic:', err);
    } finally {
      setLoading(false);
    }
  }, [diagnosticId]);

  useEffect(() => {
    fetchDiagnostic();
  }, [fetchDiagnostic]);

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    alert('Report link copied to clipboard!');
  };

  const parseReport = (reportText) => {
    if (!reportText) return null;

    const sections = {
      summary: '',
      findings: [],
      recommendations: []
    };

    const summaryMatch = reportText.match(/EXECUTIVE SUMMARY:(.*?)(?=KEY FINDINGS:|$)/s);
    if (summaryMatch) {
      sections.summary = summaryMatch[1].trim();
    }

    const findingsMatch = reportText.match(/KEY FINDINGS:(.*?)(?=RECOMMENDATIONS:|$)/s);
    if (findingsMatch) {
      const findingsText = findingsMatch[1].trim();
      const findingItems = findingsText.split(/\n\d+\./).filter(f => f.trim());
      sections.findings = findingItems.map((f, i) => {
        const parts = f.split(' - Severity:');
        const text = parts[0].trim();
        return {
          id: `finding-${i}-${text.length}-${text.slice(0, 16)}`,
          text,
          severity: parts[1] ? parts[1].trim() : 'Medium'
        };
      });
    }

    const recsMatch = reportText.match(/RECOMMENDATIONS:(.*?)$/s);
    if (recsMatch) {
      const recsText = recsMatch[1].trim();
      const recItems = recsText.split(/\n\d+\./).filter(r => r.trim());
      sections.recommendations = recItems.map((r, i) => {
        const text = r.trim();
        return { id: `rec-${i}-${text.length}-${text.slice(0, 16)}`, text };
      });
    }

    return sections;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FF6A00] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#A0A0A0]" data-testid="loading-message">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!diagnostic) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center">
        <p className="text-[#A0A0A0]" data-testid="error-message">Report not found</p>
      </div>
    );
  }

  const report = parseReport(diagnostic.ai_report);
  const severityColors = {
    'Critical': 'text-red-500 bg-red-500/10',
    'High': 'text-[#FF6A00] bg-[#FF6A00]/10',
    'Medium': 'text-yellow-500 bg-yellow-500/10',
    'Low': 'text-green-500 bg-green-500/10'
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D]">
      <Header />
      
      {/* Action Bar - Hide on print */}
      <div className="no-print sticky top-16 z-10 bg-[#1A1A1D] border-b border-[#FF6A00]/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <span className="font-mono text-xs uppercase tracking-widest text-[#A0A0A0]" data-testid="report-label">
            TRUST SIGNAL REPORT
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 bg-transparent border border-[#FF6A00]/30 text-white rounded-md hover:bg-[#FF6A00]/10 transition-colors"
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-[#FF6A00] text-white rounded-md hover:bg-[#FF6A00]/90 transition-colors"
              data-testid="print-button"
            >
              <Download className="w-4 h-4" />
              Download Report
            </button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-5xl mx-auto px-6 py-12 print-white-bg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 pt-20"
        >
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4" data-testid="report-title">
            Trust & Risk Intelligence Report
          </h1>
          <div className="flex items-center gap-4 text-sm text-[#A0A0A0]">
            <span className="font-mono" data-testid="report-id">Report ID: {diagnosticId.slice(0, 8).toUpperCase()}</span>
            <span>•</span>
            <span data-testid="report-date">{new Date(diagnostic.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </motion.div>

        {/* Executive Summary */}
        {report?.summary && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-8 rounded-lg mb-8"
          >
            <h2 className="text-2xl font-display font-bold mb-4 flex items-center gap-2" data-testid="summary-title">
              <FileText className="w-6 h-6 text-[#FF6A00]" />
              Executive Summary
            </h2>
            <div className="text-[#A0A0A0] leading-relaxed whitespace-pre-line" data-testid="summary-content">
              {report.summary}
            </div>
          </motion.section>
        )}

        {/* Key Findings */}
        {report?.findings && report.findings.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-8 rounded-lg mb-8"
          >
            <h2 className="text-2xl font-display font-bold mb-6 flex items-center gap-2" data-testid="findings-title">
              <AlertTriangle className="w-6 h-6 text-[#FF6A00]" />
              Key Findings
            </h2>
            <div className="space-y-4">
              {report.findings.map((finding, idx) => (
                <div
                  key={finding.id}
                  className="p-5 bg-[#1A1A1D] rounded-lg border border-[#FF6A00]/10"
                  data-testid={`finding-${idx}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-white leading-relaxed" data-testid={`finding-text-${idx}`}>
                        {finding.text}
                      </p>
                    </div>
                    <span
                      className={`font-mono text-xs px-3 py-1.5 rounded-full whitespace-nowrap ${severityColors[finding.severity] || 'text-[#A0A0A0] bg-[#A0A0A0]/10'}`}
                      data-testid={`finding-severity-${idx}`}
                    >
                      {finding.severity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Recommendations */}
        {report?.recommendations && report.recommendations.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-8 rounded-lg mb-8"
          >
            <h2 className="text-2xl font-display font-bold mb-6 flex items-center gap-2" data-testid="recommendations-title">
              <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
              Recommended Actions
            </h2>
            <div className="space-y-4">
              {report.recommendations.map((rec, idx) => (
                <div
                  key={rec.id}
                  className="flex items-start gap-4 p-4 bg-[#1A1A1D] rounded-lg"
                  data-testid={`recommendation-${idx}`}
                >
                  <div className="w-6 h-6 rounded-full bg-[#10B981]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#10B981] text-sm font-mono">{idx + 1}</span>
                  </div>
                  <p className="text-[#A0A0A0] leading-relaxed" data-testid={`recommendation-text-${idx}`}>
                    {rec.text}
                  </p>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* CTA Section - Hide on print */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-card p-8 rounded-lg no-print mb-8"
        >
          <div className="text-center border-b border-white/5 pb-6 mb-6">
            <p className="text-[#A0A0A0] leading-relaxed mb-4">
              <strong className="text-white">This is a surface-level discovery.</strong><br />
              Structured Review adds human interpretation, prioritisation, and revenue impact context.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/pricing"
              className="px-8 py-3 bg-[#FF6A00] text-white font-semibold rounded-md hover:bg-[#FF6A00]/90 transition-all duration-300 shadow-[0_0_20px_rgba(255,106,0,0.3)] text-center"
              data-testid="purchase-review-button"
            >
              Purchase Structured Review (\u00a3295)
            </a>
            <a
              href="/contact"
              className="px-8 py-3 bg-transparent border border-[#FF6A00]/30 text-white font-semibold rounded-md hover:bg-[#FF6A00]/10 transition-all text-center"
              data-testid="request-audit-button"
            >
              Request Pre-Release Audit
            </a>
          </div>
        </motion.section>

        {/* Return Paths */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center no-print"
        >
          <p className="text-sm text-[#A0A0A0] mb-4">Navigate</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-sm text-[#FF6A00] hover:text-[#FF6A00]/80 transition-colors">Return to Home</a>
            <a href="/scan" className="text-sm text-[#FF6A00] hover:text-[#FF6A00]/80 transition-colors">Run Another Scan</a>
            <a href="/gateway" className="text-sm text-[#FF6A00] hover:text-[#FF6A00]/80 transition-colors">Talk to Gateway</a>
            <a href="https://www.linkedin.com/company/subsurface-qa-risk/" target="_blank" rel="noopener noreferrer" className="text-sm text-[#FF6A00] hover:text-[#FF6A00]/80 transition-colors">View Real-World Breakdowns \u2192 LinkedIn</a>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default ReportPage;
